import { ProductJsonLdProps } from 'next-seo'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import useTakeRate from '../../services/useTakeRate'
import { useCart } from '../Cart/CartProvider'
import { CountryAndLocale } from '../../external'
import { getBackInStockDate, getExactStockLevel, getFeatureTypeColorObject, getFeatureTypeMaterialObject, getFeatureTypesArray, getPathName, getProductBaseStandards, getProductCertifications, getProductLabels, getProductMedia, getProductStandardVersions, isOutOfStockByDate } from '../../lib/product'
import { getMediumPreview, imageLoader } from '../../utils/images'
import { Product, Supplier } from '../../utils/types/Product'
import { getBasePathWithLocale } from '../../utils/urls'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { Currency } from '../../utils/constants'
import { getPrices } from '../../lib/getPrices'
import { AggregateRatingProductSchemaProps, ReviewMinimized } from '../../utils/types/Review'
import { convertReviewToProductSchemaVersion } from '../../utils/reviewUtils'
import { libraryFunctions } from '../../lib/resources/price'
import { getVariantType } from '../../lib/variant'
import { getSupplierDeliveryDetails } from '../../lib/service'
import { formatTimeInterval } from '../../lib/time'

const { getDefaultPrice } = libraryFunctions
const RETURN_DAYS = 14
interface UseProductSchemaPropsParams {
  product?: Product
  reviews?: ReviewMinimized[]
  aggregateRating?: AggregateRatingProductSchemaProps
  supplier?: Supplier
  productName: string
  description: string
}

const useProductSchemaProps = (params: UseProductSchemaPropsParams): ProductJsonLdProps | null => {
  const { product, reviews, aggregateRating, supplier, productName, description } = params

  const { t } = useTranslation('products')
  const { locale: countryAndLocale } = useRouter()
  const { country, locale } = getCountryAndLocaleStrings(countryAndLocale)
  const { modifiedCart } = useCart()

  const basePath = getBasePathWithLocale(countryAndLocale as CountryAndLocale)
  const productPath = product ? getPathName(product, t) : ''
  const productUrl = `${basePath}${productPath}`
  const isNonVariantProduct = product?.product && product.product.length === 0
  const calcTakeRate = useTakeRate()

  const defaultPriceObject = getDefaultPrice(product?.price || [])
  const moq = Number(defaultPriceObject?.['price.quantity']) || 1
  const colorFeatureType = getFeatureTypeColorObject(product as Product)
  const materialFeatureType = getFeatureTypeMaterialObject(product as Product)
  const features = getFeatureTypesArray(product as Product)
  const certifications = getProductCertifications(product as Product) || []
  const baseStandards = getProductBaseStandards(product as Product) || []
  const standardVersions = getProductStandardVersions(product as Product) || []
  const labels = getProductLabels(product as Product) || []
  const deliverCountries = Object.keys(product?.supplier[0]['service.config'] || {})

  const featureProperties = features.map((feature) => ({
    '@type': 'PropertyValue',
    name: feature.label,
    value: feature.value,
  }))

  const standardProperties = [
    ...certifications,
    ...baseStandards,
    ...standardVersions,
    ...labels,
  ].map((feature) => ({
    '@type': 'PropertyValue',
    name: feature['attribute.label'],
    value: 'yes',
  }))

  const {
    default: { price: defaultPrice = 0 },
    final: { price: finalPrice = 0 },
    currency: currencySymbol = '',
  } = product
    ? getPrices(
      product,
      moq, // Use MOQ to get initial final price
      {
        country,
        modifiedCart,
        calcTakeRate,
      },
    ) : { default: {}, final: {} }

  if (!defaultPrice || !finalPrice) {
    return null
  }

  const currencyCode = Object.entries(
    Currency,
  ).find((currency) => currency[1] === currencySymbol)?.[0] || ''

  const images = getProductMedia(product).map((image) => image.original)
  const minPrice = finalPrice.toFixed(2).replace(',', '.')
  const maxPrice = defaultPrice.toFixed(2).replace(',', '.')
  const {
    freeShippingLimit,
    deliveryCost,
  } = getSupplierDeliveryDetails(supplier, country, true)

  const { deliveryTime } = getSupplierDeliveryDetails(product?.supplier[0], country)
  const delivery = `${formatTimeInterval(deliveryTime.toString(), new Date().toISOString(), undefined, locale)}`
  const fromDate = Number(delivery.split('-')[0].split(' ')[1]) - 1
  const toDate = Number(delivery.split('-')[1].replace(/(th|st|nd|rd)/g, '')) - 1

  // Check if product is fully available, limited availability or out of stock
  const variants = product?.product && product.product.length > 0
    ? product.product
    : [product] // Doesn't have variants
  const outOfStockDates = variants.map((variant) => getBackInStockDate(variant))
  const someVariantsOutOfStock = outOfStockDates.some((item) => isOutOfStockByDate(item || null))
  const someVariantsInStock = outOfStockDates.some((item) => !isOutOfStockByDate(item || null))
  const variantType = getVariantType(product?.product)

  const variantData = product?.product?.map((variant) => ({
    '@type': 'Product',
    name: productName,
    sku: variant['product.code'],
    ...(variantType === 'color' ? { color: variant['product.label'] } : {}),
    ...(variantType === 'size' ? { size: variant['product.label'] } : {}),
    offers: [
      {
        '@type': 'Offer',
        price: minPrice,
        priceCurrency: currencyCode,
        url: productUrl,
        availability: getExactStockLevel(variant) === 0 ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          name: supplier?.['supplier.label'] || '',
        },
        priceSpecification: {
          '@type': 'PriceSpecification',
          valueAddedTaxIncluded: true,
          price: minPrice,
          priceCurrency: currencyCode,
          minPrice,
          maxPrice,
          priceType: 'https://schema.org/SalePrice',
        },
      }],
    shippingDetails: {
      '@type': 'OfferShippingDetails',
      shippingDestination: [
        {
          '@type': 'DefinedRegion',
          addressCountry: deliverCountries,
        },
      ],
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: deliveryCost,
        currency: currencyCode,
      },
      freeShippingThreshold: {
        '@type': 'MonetaryAmount',
        value: freeShippingLimit,
        currency: currencyCode,
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: '0', // Always 0
          maxValue: '1', // always 1
          unitCode: 'DAY',
        },
        transitTime: {
          // NEW https://schema.org/transitTime (essentially deliveryTime -1d)
          '@type': 'QuantitativeValue',
          minValue: fromDate, // low end delivery time - 1 e.g 4th-5th March
          maxValue: toDate, // same, but high end and -1
          unitCode: 'DAY',
        },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
          ],
        },
        cutoffTime: '19:30:00+02:00', // NEW cutoff time in Helsinki time (UTC+2)
      },
    },
    isVariantOf: {
      '@type': 'Product',
      name: productName,
      sku: product?.['product.code'],
    },
  }))

  const renderProductAvailability = () => {
    if (someVariantsInStock && someVariantsOutOfStock) return 'https://schema.org/LimitedAvailability'
    if (someVariantsInStock && !someVariantsOutOfStock) return 'https://schema.org/InStock'
    if (!someVariantsInStock && someVariantsOutOfStock) return 'https://schema.org/OutOfStock'
    return ''
  }

  const reviewsConverted = reviews?.map(convertReviewToProductSchemaVersion) ?? []
  const isReviewed = !!(reviewsConverted.length && Number(aggregateRating?.reviewCount ?? '0'))
  const reviewSchemaData = isReviewed
    ? {
      reviews: reviewsConverted,
      aggregateRating,
    }
    : undefined

  return {
    productName,
    description,
    brand: supplier?.['supplier.label'],
    images,
    sku: product?.['product.code'],
    manufacturerName: supplier?.['supplier.label'],
    manufacturerLogo: imageLoader(getMediumPreview(supplier?.media?.[0])),
    material: materialFeatureType.value.at(0), // Allow only one value
    additionalProperty: [
      ...featureProperties,
      ...standardProperties,
    ],
    hasVariant: variantData,
    offers: [
      {
        '@type': 'Offer',
        price: minPrice,
        priceCurrency: currencyCode,
        url: productUrl,
        availability: renderProductAvailability(),
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          name: supplier?.['supplier.label'] || '',
        },
        priceSpecification: {
          '@type': 'PriceSpecification',
          valueAddedTaxIncluded: true,
          price: minPrice,
          priceCurrency: currencyCode,
          minPrice,
          maxPrice,
          priceType: 'https://schema.org/SalePrice',
        },
      }],
    ...(isNonVariantProduct && colorFeatureType ? { color: colorFeatureType.value.at(0) } : {}),
    ...reviewSchemaData,
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: RETURN_DAYS,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnLabelSource: 'https://schema.org/ReturnLabelCustomerResponsibility',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      url: productUrl,
    },
  }
}

export default useProductSchemaProps
